/**
 * PositionSummary - Displays user's positions with P&L calculations.
 */

import { memo, useMemo } from 'react'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Position } from '../../engine/types'

export interface PositionSummaryProps {
  /** User's current positions */
  positions: Position[]
  /** Current market price (for unrealized P&L calculation) */
  currentPrice: number | null
  /** Optional className */
  className?: string
}

/** Position with calculated P&L */
interface PositionWithPnL extends Position {
  unrealizedPnL: number | null
  totalPnL: number | null
  currentValue: number | null
}

/**
 * Single position row component.
 */
const PositionRow = memo(function PositionRow({
  position,
}: {
  position: PositionWithPnL
}) {
  const { outcome, shares, averageCost, realizedPnL, unrealizedPnL, totalPnL, currentValue } =
    position

  const avgCostDisplay = Math.round(averageCost * 100)

  // Determine P&L status
  const pnlStatus =
    totalPnL === null
      ? 'neutral'
      : totalPnL > 0
        ? 'profit'
        : totalPnL < 0
          ? 'loss'
          : 'neutral'

  const PnLIcon =
    pnlStatus === 'profit'
      ? TrendingUp
      : pnlStatus === 'loss'
        ? TrendingDown
        : Minus

  return (
    <div className="rounded-md border border-border bg-background p-3">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded px-2 py-0.5 text-xs font-medium',
              outcome === 'Yes'
                ? 'bg-blue-500/20 text-blue-700'
                : 'bg-purple-500/20 text-purple-700'
            )}
          >
            {outcome}
          </span>
          <span className="text-sm font-medium text-foreground">
            {shares} shares
          </span>
        </div>

        {/* P&L indicator */}
        {totalPnL !== null && (
          <div
            className={cn(
              'flex items-center gap-1 text-sm font-medium',
              pnlStatus === 'profit' && 'text-green-600',
              pnlStatus === 'loss' && 'text-red-600',
              pnlStatus === 'neutral' && 'text-muted-foreground'
            )}
          >
            <PnLIcon className="h-4 w-4" />
            <span className="tabular-nums">
              {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">Avg Cost:</span>
          <span className="ml-1 font-mono tabular-nums text-foreground">
            {avgCostDisplay}¢
          </span>
        </div>

        {currentValue !== null && (
          <div>
            <span className="text-muted-foreground">Value:</span>
            <span className="ml-1 font-mono tabular-nums text-foreground">
              ${currentValue.toFixed(2)}
            </span>
          </div>
        )}

        {unrealizedPnL !== null && (
          <div>
            <span className="text-muted-foreground">Unrealized:</span>
            <span
              className={cn(
                'ml-1 font-mono tabular-nums',
                unrealizedPnL > 0 && 'text-green-600',
                unrealizedPnL < 0 && 'text-red-600',
                unrealizedPnL === 0 && 'text-foreground'
              )}
            >
              {unrealizedPnL >= 0 ? '+' : ''}${unrealizedPnL.toFixed(2)}
            </span>
          </div>
        )}

        {realizedPnL !== 0 && (
          <div>
            <span className="text-muted-foreground">Realized:</span>
            <span
              className={cn(
                'ml-1 font-mono tabular-nums',
                realizedPnL > 0 && 'text-green-600',
                realizedPnL < 0 && 'text-red-600'
              )}
            >
              {realizedPnL >= 0 ? '+' : ''}${realizedPnL.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
})

/**
 * PositionSummary displays user's positions and P&L.
 */
export const PositionSummary = memo(function PositionSummary({
  positions,
  currentPrice,
  className,
}: PositionSummaryProps) {
  // Calculate P&L for each position
  const positionsWithPnL: PositionWithPnL[] = useMemo(() => {
    return positions.map((position) => {
      let unrealizedPnL: number | null = null
      let currentValue: number | null = null

      if (currentPrice !== null && position.shares > 0) {
        // For Yes shares, current value is shares * currentPrice
        // For No shares, current value is shares * (1 - currentPrice)
        const priceForOutcome =
          position.outcome === 'Yes' ? currentPrice : 1 - currentPrice
        currentValue = position.shares * priceForOutcome
        const costBasis = position.shares * position.averageCost
        unrealizedPnL = currentValue - costBasis
      }

      const totalPnL =
        unrealizedPnL !== null
          ? position.realizedPnL + unrealizedPnL
          : position.realizedPnL !== 0
            ? position.realizedPnL
            : null

      return {
        ...position,
        unrealizedPnL,
        currentValue,
        totalPnL,
      }
    })
  }, [positions, currentPrice])

  // Calculate totals
  const totals = useMemo(() => {
    let totalValue = 0
    let totalUnrealized = 0
    let totalRealized = 0
    let hasUnrealized = false

    for (const pos of positionsWithPnL) {
      if (pos.currentValue !== null) {
        totalValue += pos.currentValue
      }
      if (pos.unrealizedPnL !== null) {
        totalUnrealized += pos.unrealizedPnL
        hasUnrealized = true
      }
      totalRealized += pos.realizedPnL
    }

    return {
      totalValue: positionsWithPnL.length > 0 ? totalValue : null,
      totalUnrealized: hasUnrealized ? totalUnrealized : null,
      totalRealized,
      totalPnL: hasUnrealized
        ? totalUnrealized + totalRealized
        : totalRealized !== 0
          ? totalRealized
          : null,
    }
  }, [positionsWithPnL])

  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4', className)}
      role="region"
      aria-label="Position summary"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">Your Positions</h3>
        {totals.totalPnL !== null && (
          <span
            className={cn(
              'text-sm font-medium tabular-nums',
              totals.totalPnL > 0 && 'text-green-600',
              totals.totalPnL < 0 && 'text-red-600',
              totals.totalPnL === 0 && 'text-muted-foreground'
            )}
          >
            {totals.totalPnL >= 0 ? '+' : ''}${totals.totalPnL.toFixed(2)}
          </span>
        )}
      </div>

      {positions.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground">No positions yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Buy shares to open a position
          </p>
        </div>
      ) : (
        <>
          {/* Position list */}
          <div className="space-y-2">
            {positionsWithPnL.map((position) => (
              <PositionRow
                key={`${position.marketId}-${position.outcome}`}
                position={position}
              />
            ))}
          </div>

          {/* Totals */}
          {positionsWithPnL.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Value:</span>
                <span className="font-medium tabular-nums text-foreground">
                  {totals.totalValue !== null
                    ? `$${totals.totalValue.toFixed(2)}`
                    : '—'}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Beginner explanation */}
      {positions.length > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          Unrealized P&L is your profit/loss if you sold now. Realized P&L is
          from shares you&apos;ve already sold.
        </p>
      )}
    </div>
  )
})
